import { Component, Element, h, Prop, State, Watch } from '@stencil/core' // Event, EventEmitter, Watch, Listen, Method 
import { Calendar } from '@fullcalendar/core'
import dayGridPlugin from '@fullcalendar/daygrid'
import interactionPlugin from '@fullcalendar/interaction'
import './udp-calendar-base.css'

@Component({
  tag: 'udp-calendar-base',
  styleUrl: 'udp-calendar-base.css',
  shadow: true,
})
export class UdpCalendarBase {
  calendar: Calendar
  @Element() el: HTMLElement

  @Prop() initialView: string = 'dayGridMonth'

  // SIZING SETTINGS
  @Prop() height: number = 550              // Sets the height of the entire calendar, including header and footer.
  @Prop() contentHeight: number             // Sets the height of the view area of the calendar. By default, this option is unset and the calendar’s height is calculated by aspectRatio.
  @Prop() aspectRatio: number               // A calendar is a block-level element that fills its entire available width. The calendar’s height, however, is determined by this ratio of width to height. (Hint: larger numbers make smaller heights). Default is 1.35
  @Prop() width: number                     // There is no width setting in FullCalendar so this is applied via styling

  // WHOLE-DAY SETTINGS
  @Prop() weekends: boolean = true          // Whether to include Saturday/Sunday columns in any of the calendar views.
  @Prop() hiddenDays: number[] = []         // Exclude certain days-of-the-week from being displayed.
  @Prop() dayHeaders:  boolean = false      // Whether the day headers should appear. For the Month, TimeGrid, and DayGrid views.
  @Prop() dayHeaderFormatWeekView: any      // Determines the text that will be displayed on the calendar’s column headings. // // like 'Mon 9/7', for week views { weekday: 'short', month: 'numeric', day: 'numeric', omitCommas: true }
  @Prop() dayHeaderFormatMonthView: any     // Determines the text that will be displayed on the calendar’s column headings. // like 'Mon', for month view  { weekday: 'short' }
  @Prop() dayMinWidth: number = null        // If specified, when the calendar gets narrow enough where day cells can no longer meet their dayMinWidth, horizontal scrollbars will appear.
  
  // DAY HEADER RENDER HOOKS   https://fullcalendar.io/docs/day-header-render-hooks
  @Prop() dayHeaderClassNames: any   // a ClassName Input for adding classNames to the header <th> cell
        // example return ['custom-day-header-class'];
  @Prop() dayHeaderContent: any      // a Content Injection Input. Generated content is inserted inside the inner-most wrapper of the header cell. It does not replace the <th> cell.
  @Prop() dayHeaderDidMount: any     // called right after the <th> has been added to the DOM
  @Prop() dayHeaderWillUnmount: any  // called right before the <th> will be removed from the DOM
    // When the above hooks are specified as a function in the form function(arg), the arg is an object with the following properties: date, text, isPast, isFuture, isToday, isOther, resource (if the date column lives under a specific resource in vertical resource view, this value will be the Resource Object), el (the <th> element. only available in dayHeaderDidMount and dayHeaderWillUnmount)

  // DAY CELL RENDER HOOKS   https://fullcalendar.io/docs/day-header-render-hooks
  @Prop() dayCellClassNames: any    // a ClassName Input for adding classNames to the <td> cell
  @Prop() dayCellContent: any       // a Content Injection Input. Generated content is inserted inside the inner-most wrapper of the day cell. It does not replace the <td> cell.
  @Prop() dayCellDidMount: any      // called right after the <td> has been added to the DOM
  @Prop() dayCellWillUnmount: any   // called right before the <td> will be removed from the DOM
    // When the above hooks are specified as a function in the form function(arg), the arg is an object with the following properties: date, dayNumberText, isPast, isFuture, isToday, isOther, resource (if the date column lives under a specific resource in vertical resource view, this value will be the Resource Object), el (the <th> element. only available in dayHeaderDidMount and dayHeaderWillUnmount)

  // TIME-AXIS SETTINGS
  @Prop() slotDuration: string = '00:30:00'   // The frequency for displaying time slots.
  @Prop() slotLabelInterval: string = '01:00' // The frequency that the time slots should be labelled with text.
  @Prop() slotLabelFormat: any = {            // Determines the text that will be displayed within a time slot.
    hour: 'numeric',
    minute: '2-digit',
    omitZeroMinute: true,
    meridiem: 'short'
  } 
  @Prop() slotMinTime: string = '00:00:00'  // Determines the first time slot that will be displayed for each day.
  @Prop() slotMaxTime: string = '24:00:00'  // Determines the last time slot that will be displayed for each day. In line with the discussion about the Event object, it is important to stress that this should be specified as an exclusive end time.
  @Prop() scrollTime: string = '06:00:00'   // Determines how far forward the scroll pane is initially scrolled. Default is 6am
  @Prop() scrollTimeReset: boolean = true   // Whether the view should scroll to scrollTime every time the date range changes.
  
  // Label Hooks = Where the date/time text is displayed. https://fullcalendar.io/docs/slot-render-hooks
  @Prop() slotLabelClassNames: any  // a ClassName Input
  @Prop() slotLabelContent: any     // a Content Injection Input
  @Prop() slotLabelDidMount: any
  @Prop() slotLabelWillUnmount: any
  // Lane Hooks = The long span of content next to the slot’s date/time text. In timegrid view, this is the horizontal space that passes under all of the days. In timeline view, this is the vertical space that passes through the resources.
  @Prop() slotLaneClassNames: any   // a ClassName Input
  @Prop() slotLaneContent: any      // a Content Injection Input
  @Prop() slotLaneDidMount: any
  @Prop() slotLaneWillUnmount: any
    // When the above hooks are specified as a function in the form function(arg), the arg is an object with the following properties: date, text, isPast, isFuture, isToday, el (the <td> element. only available in slotLabelDidMount, slotLabelWillUnmount, slotLaneDidMount, and slotLaneWillUnmount), level (only for slot labels, and only for timeline view when slotLabelFormat is specified as an array. Indicates which tier of the header is being rendered. 0 is the topmost.)

  // METHODS
  @Prop() scrollToTimeDuration: number      // Programatically scroll the current view to the given time. Use like this: calendar.scrollToTime( scrollToTimeDuration )
  
  // CALLBACKS
  @Prop() datesSet: any  // Called after the calendar’s date range has been initially set or changed in some way and the DOM has been updated. function( dateInfo ) https://fullcalendar.io/docs/datesSet
  
  // ADDITIONAL SETTINGS
  @Prop() firstDay: number = 0                  // The day that each week begins. 0 = Sunday
  @Prop() showNonCurrentDates: boolean = true   // In month view, whether dates in the previous or next month should be rendered at all.
  @Prop() toggleViewOptions: string = 'dayGridDay,dayGridWeek,dayGridMonth,dayGridYear'   // The options for toggling view in the upper right corner

  // DATA INPUT https://fullcalendar.io/docs/events-json-feed
  @Prop() events: any
  @Prop() eventContent: any
  @Prop() eventSources: any 
  @Prop() eventOnclick: any

  @State() calendarSettings: any = {};
  
  componentDidLoad() {
    const calendarEl = this.el.shadowRoot.querySelector('#calendar') as HTMLElement

    // CREATE SETTINGS FROM PROPS
    this.calendarSettings = {
      height: this.height,
      contentHeight: this.contentHeight,
      aspectRatio: this.aspectRatio,
      weekends: this.weekends,
      hiddenDays: this.hiddenDays,
      dayHeaders: this.dayHeaders,
      dayMinWidth: this.dayMinWidth,
      slotDuration: this.slotDuration,
      slotLabelInterval: this.slotLabelInterval,
      slotLabelFormat: this.slotLabelFormat,
      slotMinTime: this.slotMinTime,
      slotMaxTime: this.slotMaxTime,
      scrollTime: this.scrollTime,
      scrollTimeReset: this.scrollTimeReset,
      firstDay: this.firstDay,
      showNonCurrentDates: this.showNonCurrentDates,
    }

    // ADD BUTTONS TO ALLOW USER TO TOGGLE BETWEEN MONTH-WEEK VIEW
    this.calendarSettings.headerToolbar = {
      left: 'prev,next',
      center: 'title',
      right: this.toggleViewOptions,
    }

    this.calendarSettings.views = {
      dayGridWeek: {
        dayHeaderFormat: this.dayHeaderFormatWeekView ? this.dayHeaderFormatWeekView : { weekday: 'short', month: 'numeric', day: 'numeric', omitCommas: true, omitZeroMinute: true, meridiem: 'short' }
      },
      dayGridMonth: {
        dayHeaderFormat: this.dayHeaderFormatMonthView ? this.dayHeaderFormatMonthView : { weekday: 'short' }
      }
    }

    // ADD HOOKS TO SETTINGS
    const hooks = [
      { prop: 'dayHeaderClassNames', setting: 'dayHeaderClassNames' },
      { prop: 'dayHeaderContent', setting: 'dayHeaderContent' },
      { prop: 'dayHeaderDidMount', setting: 'dayHeaderDidMount' },
      { prop: 'dayHeaderWillUnmount', setting: 'dayHeaderWillUnmount' },
      { prop: 'dayCellClassNames', setting: 'dayCellClassNames' },
      { prop: 'dayCellContent', setting: 'dayCellContent' },
      { prop: 'dayCellDidMount', setting: 'dayCellDidMount' },
      { prop: 'dayCellWillUnmount', setting: 'dayCellWillUnmount' },
      { prop: 'slotLabelClassNames', setting: 'slotLabelClassNames' },
      { prop: 'slotLabelContent', setting: 'slotLabelContent' },
      { prop: 'slotLabelDidMount', setting: 'slotLabelDidMount' },
      { prop: 'slotLabelWillUnmount', setting: 'slotLabelWillUnmount' },
      { prop: 'slotLaneClassNames', setting: 'slotLaneClassNames' },
      { prop: 'slotLaneContent', setting: 'slotLaneContent' },
      { prop: 'slotLaneDidMount', setting: 'slotLaneDidMount' },
      { prop: 'slotLaneWillUnmount', setting: 'slotLaneWillUnmount' },
    ];
    hooks.forEach(({ prop, setting }) => {
      if (this[prop]) {
        this.calendarSettings[setting] = (arg) => this[prop](arg);
      }
    });


    // ADD EVENTS TO SETTINGS
    this.calendarSettings.events = this.events
    this.calendarSettings.eventSources = this.eventSources
    this.calendarSettings.eventContent = this.eventContent


    // ADD ONCLICK FUCNTIONS TO EVENTS
    this.calendarSettings.eventClick = (event) => this.eventOnclick(event)


    // ASSIGN SETTINGS AND CREATE CALENDAR
    this.calendar = new Calendar(calendarEl, {
      plugins: [dayGridPlugin, interactionPlugin],
      initialView: this.initialView,
      ...this.calendarSettings
    });
    this.calendar.render()

    // Programatically scroll the current view to the given time. https://fullcalendar.io/docs/scrollToTime
    if (this.scrollToTimeDuration) (
      this.calendar.scrollToTime(this.scrollToTimeDuration)
    )
  }

  @Watch('events')
  eventsChanged(newValue: any) {
    if (this.calendar) {
      this.calendar.removeAllEvents();
      this.calendar.addEventSource(newValue);
    }
  }

  disconnectedCallback() {
    if (this.calendar) {
      this.calendar.destroy()
    }
  }

  render() {
    const calendarStyle = {
      ...(this.width !== null ? { width: this.width + 'px' } : {}) // Conditionally add width property
    };

    return (
      <div id='calendar' style={calendarStyle}></div>
    )
  }
}
